import React, { useEffect, useState } from "react";
import { Plus, Send } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Typography } from "@/components/ui/atoms/Typography";
import { Avatar } from "@/components/ui/atoms/Avatar";
import { IconWrapper } from "@/components/ui/atoms/IconWrapper";
import { TableRow, TableCell, TableBody, Table } from "@/components/ui/atoms/Table";
import { Tag } from "@/components/ui/atoms/Tag";
import { Button } from "@/components/ui/atoms/Button";
import { Field, FieldLabel } from "@/components/ui/molecules/Field";
import { InputGroup, InputGroupInput, InputGroupAddon, InputGroupButton } from "@/components/ui/molecules/InputGroup";
import { Popover } from "@/components/ui/Popover";
import { WorkspaceSettingsTemplate } from "@/components/ui/templates/WorkspaceSettingsTemplate";
import { useWorkspace } from "@/hooks/useWorkspace";
import { formatDate } from "@/utils/date";
import { SendInvitationConfirmationDialog } from "./SendInvitationConfirmationDialog";

interface WorkspaceSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export const WorkspaceSettings = ({ isOpen, onClose }: WorkspaceSettingsProps) => {
  const { t } = useTranslation('settings');
  const { data: workspace, isLoading, inviteMutation } = useWorkspace({ enabled: isOpen });
  const members = workspace?.members ?? [];
  const [inviteEmail, setInviteEmail] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isInviteFormOpen, setIsInviteFormOpen] = useState(false);
  const [isInviteConfirmOpen, setIsInviteConfirmOpen] = useState(false);
  const [pendingInviteEmail, setPendingInviteEmail] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setIsInviteFormOpen(false);
      setInviteEmail("");
      setIsInviteConfirmOpen(false);
      setPendingInviteEmail("");
    }
  }, [isOpen]);

  const filteredMembers = members.filter(
    (m) =>
      !searchQuery.trim() ||
      (m.full_name ?? "").toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
      m.email.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  const handleConfirmInvite = async () => {
    const email = pendingInviteEmail.trim();
    if (!email) return;

    try {
      await inviteMutation.mutateAsync(email);
      toast.success(t('invite.success', { email }), { duration: 3000 });
      setInviteEmail("");
      setPendingInviteEmail("");
      setIsInviteConfirmOpen(false);
      setIsInviteFormOpen(false);
    } catch (error) {
      setIsInviteConfirmOpen(false);
      toast.error(error instanceof Error && error.message.trim() ? error.message : t('invite.error'));
    }
  };

  const handleOpenInviteConfirm = () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setPendingInviteEmail(email);
    setIsInviteFormOpen(false);
    setIsInviteConfirmOpen(true);
  };

  const name = workspace?.workspace_name ?? "Workspace";
  const initials = name;
  const profileCardProps = {
    initials,
    name,
    status: workspace?.workspace_status ?? "Active",
    memberCount: members.length,
    planName: workspace?.plan_name ? workspace.plan_name : "No plan",
    planExpired: workspace?.plan_expired ?? false,
    planExpiresAt: formatDate(workspace?.plan_expires_at),
    cvUsed: workspace?.cv_used ?? 0,
    cvLimit: workspace?.cv_limit ?? 0,
    interviewUsed: workspace?.interview_used ?? 0,
    interviewLimit: workspace?.interview_limit ?? 0,
  };

  const triggerRef = React.useRef<HTMLDivElement>(null);

  const handleCloseInviteForm = () => {
    setIsInviteFormOpen(false);
    setInviteEmail("");
  };

  const handleCancelInviteConfirm = () => {
    if (inviteMutation.isPending) return;
    setIsInviteConfirmOpen(false);
    setPendingInviteEmail("");
    setInviteEmail("");
  };

  const action = (
    <div ref={triggerRef} className="relative flex-shrink-0">
      <Button
        label="Add member"
        rightIcon={
          <IconWrapper size="sm" ariaHidden>
            <Plus />
          </IconWrapper>
        }
        intent="primary"
        type="contained"
        size="md"
        onClick={() => setIsInviteFormOpen(!isInviteFormOpen)}
      />
      <Popover
        open={isInviteFormOpen}
        onClose={handleCloseInviteForm}
        anchorRef={triggerRef}
        className="right-0"
        contentClassName="w-80 bg-white rounded-xl shadow-lg p-3"
      >
        <Field>
          <FieldLabel htmlFor="invite-email">Invite by email</FieldLabel>
          <InputGroup size="md">
            <InputGroupInput
              id="invite-email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleOpenInviteConfirm();
                if (e.key === "Escape") handleCloseInviteForm();
              }}
              placeholder="name@company.com"
              rounded="full"
              autoFocus
            />
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                type="icon"
                icon={<Send />}
                intent="secondary"
                className="bg-dark-invirtus hover:bg-dark-invirtus/90 text-white"
                onClick={handleOpenInviteConfirm}
                disabled={!inviteEmail.trim() || inviteMutation.isPending}
              />            
            </InputGroupAddon>
          </InputGroup>
        </Field>
      </Popover>
    </div>
  );

  const listPanelProps = {
    title: "Team members",
    count: members.length,
    searchValue: searchQuery,
    onSearchChange: setSearchQuery,
    action,
    children: filteredMembers.map((member) => {
      const displayName = member.full_name ?? member.email.split("@")[0];
      return (
        <Table>
          <TableBody>
            <TableRow key={member.id}>
              <TableCell className="w-0">
                <Avatar initials={displayName} variant="neutral" size="md" />
              </TableCell>
              <TableCell align="left">
                <Typography variant="body-sm" weight="medium" color="primary">
                  {displayName}
                </Typography>
                <Typography variant="body-xs" weight="medium" color="muted">
                  {member.email}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Tag
                  intent={
                    member.role === "you" ? "primary" : member.role === "member" ? "success" : "secondary"
                  }
                  variant="subtle"
                >
                  {member.role}
                </Tag>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      );
    }),
  };

  return (
    <>
      <WorkspaceSettingsTemplate
        isOpen={isOpen}
        onClose={onClose}
        loading={isLoading}
        profileCardProps={profileCardProps}
        listPanelProps={listPanelProps}
      />
      <SendInvitationConfirmationDialog
        open={isInviteConfirmOpen}
        email={pendingInviteEmail}
        isPending={inviteMutation.isPending}
        onClose={handleCancelInviteConfirm}
        onConfirm={handleConfirmInvite}
      />
    </>
  );
};
